import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Zap, ArrowRight, Check, X } from "lucide-react";
import { api } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** Touchpoint 🅰 — Dashboard upsell that turns Pulse into a one-click add-on for
 *  any broadcaster's station. Auto-prefills name/niche/keywords from the
 *  selected station so the friction is ~zero. */
export default function StationPulseUpsell({ station }) {
  const navigate = useNavigate();
  const [linkedSite, setLinkedSite] = useState(undefined); // undefined=loading, null=none, object=exists
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [err, setErr] = useState("");

  // Look up whether this station already has a linked Pulse site
  useEffect(() => {
    if (!station?.station_id) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/pulse/sites", { headers: authHeaders() });
        const match = (data.sites || []).find(s => s.linked_station_id === station.station_id);
        if (alive) setLinkedSite(match || null);
      } catch (_) {
        if (alive) setLinkedSite(null);
      }
    })();
    return () => { alive = false; };
  }, [station?.station_id]);

  // If linked site already exists, render a compact "open it" card
  if (linkedSite) {
    return (
      <div className="fl-card p-5 flex flex-wrap items-center justify-between gap-3" data-testid="pulse-upsell-linked">
        <div className="min-w-0 flex-1">
          <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-fuchsia-400">// Pulse · SEO</div>
          <div className="text-white font-bold mt-1 truncate">{linkedSite.name}</div>
          <div className="text-xs text-slate-400 mt-1">
            {linkedSite.published_count || 0} published · {(linkedSite.keywords || []).length} keywords targeted
          </div>
        </div>
        <button
          onClick={() => navigate(`/pulse/${linkedSite.site_id}`)}
          className="fl-btn ghost"
          data-testid="pulse-upsell-open-linked"
        >
          <Sparkles className="w-4 h-4 text-fuchsia-400" /> Open Pulse site <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (linkedSite === undefined) return null; // still loading

  const createPulseSite = async () => {
    setErr("");
    if (!accepted) { setErr("Please accept the Pulse usage terms first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/pulse/sites/from-station/${station.station_id}`,
        { accepted_terms: true },
        { headers: authHeaders() },
      );
      // Jump straight to the freshly created Pulse site so the broadcaster can
      // hit Generate immediately.
      navigate(`/pulse/${data.site.site_id}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't create Pulse site. Try again.");
      setBusy(false);
    }
  };

  // Pitch state — single CTA
  if (!showWizard) {
    return (
      <div
        className="fl-card p-6 relative overflow-hidden"
        style={{ boxShadow: "0 0 60px -20px rgba(232,121,249,0.30)" }}
        data-testid="pulse-upsell-pitch"
      >
        <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-fuchsia-500/20 blur-3xl pointer-events-none" />
        <div className="flex flex-wrap items-start justify-between gap-4 relative">
          <div className="min-w-0 flex-1">
            <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-fuchsia-300 inline-flex items-center gap-1.5">
              <Zap className="w-3 h-3" /> // Boost your station with SEO
            </div>
            <div className="fl-display text-xl sm:text-2xl font-bold text-white mt-2">
              Get found on Google, Bing &amp; ChatGPT.
            </div>
            <p className="text-slate-300/80 text-sm mt-2 max-w-xl leading-relaxed">
              Pulse writes long-form SEO articles about <strong className="text-white">{station.name}</strong> on
              autopilot — Claude does the writing, Nano Banana paints the cover art.
              People searching for {(station.genre || "music").toLowerCase()} radio find you. They click. They tune in.
            </p>
          </div>
          <button
            onClick={() => setShowWizard(true)}
            className="fl-btn flex-shrink-0"
            data-testid="pulse-upsell-cta"
          >
            <Sparkles className="w-4 h-4" /> Create my Pulse site
          </button>
        </div>
      </div>
    );
  }

  // Wizard state — preview pre-filled values + terms checkbox
  const previewKeywords = [
    `${(station.genre || "music").toLowerCase()} internet radio`,
    `how to listen to ${(station.genre || "music").toLowerCase()} radio online`,
    `best ${(station.genre || "music").toLowerCase()} radio stations`,
    `${station.name} radio station`,
    `free ${(station.genre || "music").toLowerCase()} radio ${station.country || "US"}`,
  ];

  return (
    <div className="fl-card p-6 relative" data-testid="pulse-upsell-wizard">
      <button
        onClick={() => setShowWizard(false)}
        className="absolute top-4 right-4 text-slate-400 hover:text-white"
        aria-label="Close"
        data-testid="pulse-upsell-close"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-fuchsia-300 inline-flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" /> // Pre-built for {station.name}
      </div>
      <div className="fl-display text-xl font-bold text-white mt-2 mb-4">Confirm &amp; activate.</div>

      <div className="space-y-3 text-sm">
        <div>
          <div className="text-xs uppercase tracking-widest text-white/40">Site name</div>
          <div className="text-white mt-0.5">{station.name} Blog</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-white/40">Niche</div>
          <div className="text-white/85 mt-0.5">{station.tagline || `${station.genre || "music"} internet radio station`}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-white/40">Auto-seeded keywords</div>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {previewKeywords.map(k => (
              <span key={k} className="text-xs px-2 py-0.5 rounded-full bg-fuchsia-500/15 border border-fuchsia-400/30 text-fuchsia-100">{k}</span>
            ))}
          </div>
          <div className="text-xs text-white/40 mt-2">You can edit these anytime from the Pulse site detail page.</div>
        </div>
      </div>

      <label className="flex items-start gap-3 mt-5 cursor-pointer rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid="pulse-upsell-terms-label">
        <input
          type="checkbox"
          checked={accepted}
          onChange={e => setAccepted(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-fuchsia-500 flex-shrink-0"
          data-testid="pulse-upsell-accept-terms"
        />
        <span className="text-xs text-white/65 leading-relaxed">
          I will publish Pulse-generated posts only on websites I own or operate. I will not
          resell, sub-license, or offer Pulse output to the public as a service.
        </span>
      </label>

      {err && <div className="text-rose-400 text-sm mt-3" data-testid="pulse-upsell-error">{err}</div>}

      <div className="flex flex-wrap gap-2 mt-5">
        <button
          onClick={createPulseSite}
          disabled={busy || !accepted}
          className="fl-btn"
          data-testid="pulse-upsell-create"
        >
          <Check className="w-4 h-4" /> {busy ? "Creating…" : "Activate Pulse site"}
        </button>
        <button onClick={() => setShowWizard(false)} className="fl-btn ghost" data-testid="pulse-upsell-cancel">
          Not now
        </button>
      </div>
    </div>
  );
}
